import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO, addDays, subDays } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  FiSearch,
  FiEdit2,
  FiMessageCircle,
  FiTv,
  FiPlus,
  FiTrash2,
  FiCalendar,
  FiPlay,
  FiAlertTriangle,
  FiArrowLeft,
} from 'react-icons/fi';

import api from 'services/api';
import { DataFilter, Tipo } from 'utils/variables';
import { useModal } from 'hooks/modal';
import getValidationErrors from 'services/getValidationErros';

import { show } from 'store/modules/dialog/actions';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import NoSearch2 from 'assets/nosearch2.svg';

import Button from 'components/Button';
import Card from 'components/Card';
import Input from 'components/Input';
import Flag from 'components/Flag';
import SelectDate from 'components/SelectDate';
import NotFound from 'components/NotFound';
import Avatar from 'components/Avatar';
import DatePicker from 'components/DatePicker';
import TextArea from 'components/TextArea';
import Tooltip from 'components/Tooltip';
import Checkbox from 'components/Checkbox';
import Loader from 'components/ContentLoader/GenericLoader';

import {
  Container,
  ExibicaoModal,
  ExibicaoTable,
  Filter,
  FilterItem,
  Scroll,
  Table,
  TdFlag,
  Veiculacao,
  VeiculacaoForm,
} from './styles';

import { Chamada, ChamadaExibicao, Programa } from 'pages/@types';
import AppError from 'utils/AppError';
import Select from 'components/Select';
import PlayerBaixa from 'components/PlayerBaixa';

interface Exibicao extends ChamadaExibicao {
  retranca: string;
  codmaterial: number;
  datainicio: string;
  datafim: string;
}

interface ChamadaComStatusGeral extends Chamada {
  statusgeral: {
    nome:
    | 'PENDENTE'
    | 'FILA ENVIO EXIBIDOR'
    | 'GRAVADO'
    | 'ERRO'
    | 'ENVIANDO EXIBIDOR'
    | 'NA';
    type:
    | 'pendente'
    | 'fila_envio'
    | 'gravado'
    | 'erro'
    | 'enviando_exibidor'
    | 'NA';
  };
  veiculacaoalterada?: boolean;
}

// configuração do validator
const schema = Yup.object().shape({
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início inválida'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data fim inválida'),
});

const schemaExibicao = Yup.object().shape({
  dataexibicao: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data inválida'),
  programa: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Programa inválido'),
});

export default function ChamadasValidas() {
  const statusOptions = [
    { id: 'gravados', label: 'GRAVADOS' },
    { id: 'naogravados', label: 'NÃO GRAVADOS' },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentDate, emissora } = useSelector((state: any) => state.usuario);
  const { showModal, hideModal } = useModal();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingProgramas, setLoadingProgramas] = useState(false);

  const [programas, setProgramas] = useState([]);
  const [chamadas, setChamadas] = useState<ChamadaComStatusGeral[]>([]);
  const [exibicoes, setExibicoes] = useState<Exibicao[]>([]);

  const [chamadasFiltradas, setChamadasFiltradas] = useState<
    ChamadaComStatusGeral[]
  >([]);

  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });

  const [filtro, setFiltro] = useState('');
  const [tiposFiltrados, setTiposFiltrados] = useState(
    DataFilter.tipos.map(t => t.id),
  );
  const [statusFiltrados, setStatusFiltrados] = useState(
    statusOptions.map(s => s.id),
  );

  /**
   * USE REF
   */
  const filterRef = useRef<FormHandles>(null);
  const formPeriodoVeiculacaoRef = useRef<FormHandles>(null);
  const formExibicao = useRef<FormHandles>(null);

  /**
   * USE MEMO
   */
  // para registro na tabela
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    }
  }, [currentDate]);

  // Compara data de exibicao com a data atual da página e mostra na tabela o total de exibicoes diarias
  const currentDateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), 'yyyy-MM-dd');
    }
    return null;
  }, [currentDate]);

  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  /**
   * USE CALLBACK
   */
  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleRessalva = useCallback(
    obseng => {
      const contentHTML = `<p>Observação: ${obseng}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        confirmButtonText: 'FECHAR',
        showCancelButton: false,
        showConfirmButton: true,
      };
      dispatch(show(options));
    },
    [dispatch],
  );

  const handleObservacaoExibicao = useCallback(
    observacaoexibicao => {
      const contentHTML = `<p>Observação: ${observacaoexibicao}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        confirmButtonText: 'FECHAR',
        showCancelButton: false,
        showConfirmButton: true,
      };
      dispatch(show(options));
    },
    [dispatch],
  );

  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleSubmitPeriodoVeiculacao = useCallback(
    async (data, id) => {
      if (formPeriodoVeiculacaoRef.current) {
        setLoadingSubmit(true);
        try {
          await schema.validate(data, { abortEarly: false });
          const response = await api.patch(
            `/chamadasts/${id}/periodoveiculacao`,
            data,
          );
          if (response?.data?.chamada) {
            setChamadas(prevState =>
              prevState.map(p =>
                p.id === id
                  ? {
                    ...p,
                    datafim: response.data.chamada.datafim,
                    datainicio: response.data.chamada.datainicio,
                    observacaoexibicao:
                      response.data.chamada.observacaoexibicao,
                    veiculacaoalterada: true,
                  }
                  : p,
              ),
            );
            toast.success('Informações salvas com sucesso');
          } else toast.error('Falha de comunicação com o sistema');

          hideModal();
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formPeriodoVeiculacaoRef.current.setErrors(errors);
          }
          if (error instanceof Error) toast.error(error.message);
          else if (error instanceof AppError) toast.error(error.message);
          else {
            const appError = error as AppError;
            if (appError.message)
              toast.error(appError.message, {
                toastId: appError.message,
              });
            else toast.error('Erro inesperado');
          }
        } finally {
          setLoadingSubmit(false);
        }
      }
    },
    [hideModal],
  );

  const handleSinalizaExibicao = useCallback(
    async (data, chamada) => {
      if (formExibicao.current) {
        setLoadingSubmit(true);
        try {
          formExibicao.current.setErrors({});

          await schemaExibicao.validate(data, { abortEarly: false });
          const response = await api.post<ChamadaExibicao>(`/chamadaexibicao`, {
            chamada_id: chamada.id,
            programa_id: data.programa,
            dataexibicao: format(new Date(data.dataexibicao), 'yyyy-MM-dd'),
          });
          if (response.data) {
            setChamadas(prev =>
              prev.map(ch =>
                ch.id === chamada.id
                  ? {
                    ...ch,
                    chamadaexibicao: [
                      ...(ch.chamadaexibicao || []),
                      response.data,
                    ],
                  }
                  : ch,
              ),
            );
            setExibicoes(prevState => [
              ...prevState,
              {
                ...response.data,
                retranca: chamada.retranca,
                codmaterial: chamada.codmaterial,
                datainicio: chamada.datainicio,
                datafim: chamada.datafim,
              },
            ]);
            toast.success('Informações salvas com sucesso');
            hideModal();
          } else {
            toast.error('Falha de comunicação com o sistema');
          }
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formExibicao.current.setErrors(errors);
          }
          if (error instanceof Error) toast.error(error.message);
          else if (error instanceof AppError) toast.error(error.message);
          else {
            const appError = error as AppError;
            if (appError.message)
              toast.error(appError.message, {
                toastId: appError.message,
              });
            else toast.error('Erro inesperado');
          }
        } finally {
          setLoadingSubmit(false);
        }
      }
    },
    [hideModal],
  );

  const renderPeriodoVeiculacao = useCallback(
    chamada => {
      return (
        <VeiculacaoForm
          key={`form-${chamada.id}`}
          ref={formPeriodoVeiculacaoRef}
          onSubmit={data => handleSubmitPeriodoVeiculacao(data, chamada.id)}
          initialData={{
            datainicio: new Date(chamada.datainicio),
            datafim: new Date(chamada.datafim),
            observacaoexibicao: chamada.observacaoexibicao,
          }}
        >
          <div>
            <div>
              <section>
                <h5>Data Início: *</h5>
              </section>
              <DatePicker
                placeholderText="DE"
                name="datainicio"
                disabled={loadingSubmit}
              />
            </div>
            <div>
              <section>
                <h5>Data fim: *</h5>
              </section>
              <DatePicker
                placeholderText="ATÉ"
                name="datafim"
                disabled={loadingSubmit}
              />
            </div>
          </div>
          <div>
            <section>
              <h5>Observações:</h5>
            </section>
            <TextArea
              placeholder="Obs Exibição"
              name="observacaoexibicao"
              height="calc(90vh - 700px)"
              disabled={loadingSubmit}
              errorField=""
              icon=""
            />
          </div>
          <div className="actions">
            <Button type="submit" color="primary" disabled={loadingSubmit}>
              CONFIRMAR
            </Button>
          </div>
        </VeiculacaoForm>
      );
    },
    [handleSubmitPeriodoVeiculacao, loadingSubmit],
  );

  const handleEditPeriodoVeiculacao = useCallback(
    chamada => {
      showModal({
        title: chamada.retranca,
        component: renderPeriodoVeiculacao(chamada),
        width: '40vw',
      });
    },
    [renderPeriodoVeiculacao, showModal],
  );

  /**
 * USE EFFECT
 */
  useEffect(() => {
    setChamadasFiltradas(
      chamadas.filter(
        ch =>
          ch.retranca.includes(filtro.toUpperCase()) ||
          ch.codmaterial?.toString().includes(filtro),
      ),
    );
  }, [chamadas, filtro]);

  useEffect(() => {
    setChamadasFiltradas(
      chamadas.filter(
        ch =>
          tiposFiltrados.includes(ch.tipo) &&
          (statusFiltrados.includes('gravados') ||
            ch.statusgeral.type !== 'gravado') &&
          (statusFiltrados.includes('naogravados') ||
            ch.statusgeral.type === 'gravado'),
      ),
    );
  }, [chamadas, tiposFiltrados, statusFiltrados]);

  const handleDeleteExibicao = useCallback(
    (exibicao, chamada) => {
      setSelectedId(exibicao.id);
      const optionBase = {
        type: 'question',
        action: 'success',
        contentHTML: `Remover exibição de <b>${chamada.retranca}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/chamadaexibicao/${exibicao.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Exibição removida/cancelada!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handlePlayer = useCallback(
    midia => {
      if (!!midia && midia.preview_url) {
        setPlayer({
          open: !player.open,
          midia_url: midia.preview_url,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  const renderExibicao = useCallback(
    chamada => {
      return (
        <ExibicaoModal>
          <Scroll height="35vh">
            {!!chamada.chamadaexibicao &&
              chamada.chamadaexibicao?.map(e => (
                <div key={e.id}>
                  <h4>{`${e.dataexibicao} | ${e.programa.nome} `}</h4>
                  <Button
                    type="button"
                    fill
                    color="font"
                    round
                    disabled={loadingSubmit}
                    onClick={() => handleDeleteExibicao(e, chamada)}
                  >
                    <FiTrash2 />
                  </Button>
                </div>
              ))}
          </Scroll>
          <Form
            ref={formExibicao}
            initialData={{ data: new Date() }}
            onSubmit={data => handleSinalizaExibicao(data, chamada)}
          >
            <DatePicker
              name="dataexibicao"
              disabled={loadingSubmit}
              placeholderText="Data de Exibição"
              maxDate={new Date()}
            />
            <Select
              errorField=""
              name="programa"
              placeholder="Selecione um programa ..."
              options={programas}
              isSearchable
              isClearable
              disabled={loadingProgramas || loadingSubmit}
              fill={false}
            />
            <Button
              type="submit"
              fill
              color="font"
              round
              disabled={loadingSubmit}
            >
              <FiPlus />
            </Button>
          </Form>
        </ExibicaoModal>
      );
    },
    [
      handleSinalizaExibicao,
      handleDeleteExibicao,
      loadingProgramas,
      loadingSubmit,
      programas,
    ],
  );

  const handleExibicao = useCallback(
    chamada => {
      showModal({
        title: chamada.retranca,
        component: renderExibicao(chamada),
        width: '40vw',
      });
    },
    [renderExibicao, showModal],
  );
  const renderExibicoesHoje = useCallback(() => {
    return (
      <Scroll>
        <ExibicaoTable>
          <thead>
            <tr>
              <th>CM</th>
              <th>RETRANCA</th>
              <th>PROGRAMA</th>
              <th>VEICULAÇÃO</th>
            </tr>
          </thead>
          <tbody>
            {exibicoes
              .filter(e => e.dataexibicao === currentDateFormatted)
              .map(e => (
                <tr key={e.id}>
                  <td>{e.codmaterial ? e.codmaterial : '-'}</td>
                  <td>{e.retranca}</td>
                  <td>{e.programa.nome}</td>
                  <td>
                    {e.datainicio && e.datafim
                      ? `de ${format(
                        parseISO(e.datainicio),
                        'dd/MM',
                      )} a ${format(parseISO(e.datafim), 'dd/MM/yyyy')}`
                      : '-'}
                  </td>
                </tr>
              ))}
          </tbody>
        </ExibicaoTable>
      </Scroll>
    );
  }, [currentDateFormatted, exibicoes]);

  const handleExibicoesHoje = useCallback(() => {
    const hasExibicoesNaData = (exibicoes ?? []).some(
      e => currentDateFormatted === e.dataexibicao,
    );
    if (hasExibicoesNaData) {
      showModal({
        title: `EXIBIÇÕES EM ${dateFormatted?.toUpperCase()} - ${emissora.label
          }`,
        component: renderExibicoesHoje(),
        width: '70vw',
      });
    } else {
      toast.error(`Nenhuma exibição encontrada na data ${dateFormatted}`);
    }
  }, [
    currentDateFormatted,
    dateFormatted,
    emissora.label,
    exibicoes,
    renderExibicoesHoje,
    showModal,
  ]);

  useEffect(() => {
    if (!callbackDialog?.success || !selectedId) return;
    if (callbackDialog.success.action === 'success') {
      setChamadas(prev =>
        prev.map(ch => ({
          ...ch,
          chamadaexibicao: (ch.chamadaexibicao || []).filter(
            e => e.id !== selectedId,
          ),
        })),
      );
      setExibicoes(prev =>
        prev.filter(e => e.id !== selectedId),
      );
      hideModal();
      setSelectedId(null);
    }
  }, [callbackDialog, hideModal, selectedId]);

  useEffect(() => {
    async function loadOptions() {
      try {
        const response = await api.get('/programas');
        if (response?.data) {
          setProgramas(
            response.data.map((pgm: Programa) => {
              return {
                locucaobase: pgm.locucaobase,
                value: pgm.id,
                label:
                  (pgm.sigla ? pgm.sigla + ' - ' : '') +
                  (pgm.mnemonico ? pgm.mnemonico : '') +
                  (pgm.nome ? ' (' + pgm.nome + ')' : ''),
              };
            }),
          );
        } else toast.error('Falha de comunicação com o sistema');
        setLoadingProgramas(false);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      }
    }
    loadOptions();
  }, []);

  useEffect(() => {
    const options = {
      type: 'warning',
      contentHTML: `<h4>ATENÇÃO<h4><h1>PRAÇA SELECIONADA: ${emissora.label}</h1>`,
      allowOutsideClick: false,
      cancelButtonText: 'OK',
      showConfirmButton: false,
    };
    dispatch(show(options));
  }, [dispatch, emissora.label]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadChamadas() {
      setLoading(true);
      try {
        const dataChamadas = format(new Date(currentDate), 'yyyy-MM-dd');
        const response = await api.get('/dashboard/chamadasvalidas', {
          signal: controller.signal,
          params: {
            data: dataChamadas,
            emissora_id: emissora.value,
          },
        });
        if (response && response.data) {
          setChamadas(response.data);
          setChamadasFiltradas(response.data);

          setExibicoes(
            response.data
              .filter(ch => (ch.chamadaexibicao?.length ?? 0) > 1)
              .flatMap(ch =>
                ch.chamadaexibicao.map(e => ({
                  ...e,
                  retranca: ch.retranca,
                  codmaterial: ch.codmaterial,
                  datainicio: ch.datainicio,
                  datafim: ch.datafim,
                })),
              ),
          );
        } else toast.error('Falha de comunicação com o sistema');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoading(false);
      }
    }
    loadChamadas();
    return () => {
      controller.abort();
    };
  }, [currentDate, emissora.value]);

  return (
    <Container>
      <nav>
        <Button size="lg" color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>
        {dateFormatted && new Date(currentDate) && (
          <SelectDate
            dateFormatted={dateFormatted}
            currentDateZoned={String(new Date(currentDate))}
            handleNextDay={handleNextDay}
            handleDateChange={handleDateChange}
            handlePrevDay={handlePrevDay}
          />
        )}
        <div />
      </nav>
      <Card>
        <header>
          <h4>LISTA DE CHAMADAS VÁLIDAS</h4>
          <div>
            <Form onSubmit={() => { }} ref={filterRef}>
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
                onChange={e => setFiltro(e.target.value)}
              />
            </Form>
          </div>
        </header>
        <Form
          onSubmit={() => { }}
          initialData={{
            tipo: DataFilter.tipos.map(t => t.id),
            status: statusOptions.map(s => s.id),
          }}
        >
          <Filter>
            <FilterItem name="tipo">
              <legend>Tipos</legend>
              <Checkbox
                name="tipo"
                options={DataFilter.tipos}
                handleSelected={value => {
                  if (value.checked)
                    setTiposFiltrados(prevState => [...prevState, value.id]);
                  else
                    setTiposFiltrados(prevState =>
                      prevState.filter(p => p !== value.id),
                    );
                }}
                disabled={loading}
              />
            </FilterItem>
            <FilterItem name="status">
              <legend>Status</legend>
              <Checkbox
                name="status"
                options={statusOptions}
                handleSelected={value => {
                  if (value.checked)
                    setStatusFiltrados(prevState => [...prevState, value.id]);
                  else
                    setStatusFiltrados(prevState =>
                      prevState.filter(p => p !== value.id),
                    );
                }}
                disabled={loading}
              />
            </FilterItem>
          </Filter>
        </Form>
        {loading ? (
          <Loader />
        ) : chamadasFiltradas && chamadasFiltradas.length ? (
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>ID</th>
                  <th>CM</th>
                  <th>RETRANCA</th>
                  <th>
                    <div>
                      EXIBIÇÕES
                      <Button
                        id={`id_exibicoes`}
                        fill
                        color="font"
                        round
                        onClick={() => handleExibicoesHoje()}
                      >
                        <FiCalendar />
                      </Button>
                      <Tooltip target={`id_exibicoes`} position="right">
                        EXIBIÇÕES DE {dateFormatted?.toUpperCase()}
                      </Tooltip>
                    </div>
                  </th>
                  <th>STATUS</th>
                  <th>TIPO</th>
                  <th>SETOR</th>
                  <th>VEICULAÇÃO</th>
                  <th>AVALIADOR</th>
                  <th>EXIB.</th>
                  <th>ENG.</th>
                  <th>VER</th>/
                </tr>
              </thead>
              <tbody>
                {!!chamadasFiltradas.length &&
                  chamadasFiltradas.map(ch => (
                    <tr key={ch.id}>
                      <TdFlag $isRede={ch.chamadaemissora.length === 8}>
                        {ch.chamadaemissora && ch.chamadaemissora.length === 8 && (
                          <div id={`id${ch.id}`}>
                            <Tooltip target={`id${ch.id}`} position="right">
                              <p>
                                <strong>REDE</strong>
                              </p>
                            </Tooltip>
                          </div>
                        )}
                      </TdFlag>
                      <td>{ch.id}</td>
                      <td>{ch.codmaterial || '-'}</td>
                      <td>{ch.retranca}</td>
                      <td>
                        {ch.chamadaexibicao?.length ?? 0}
                        <Button
                          id={`id_exib_${ch.id}`}
                          fill
                          color="font"
                          round
                          onClick={() => handleExibicao(ch)}
                        >
                          <FiTv />
                        </Button>
                        <Tooltip target={`id_exib_${ch.id}`} position="right">
                          EXIBIÇÕES DE: {ch.retranca}
                        </Tooltip>
                      </td>
                      <td>
                        <Flag
                          type={ch.statusgeral.type}
                          name={ch.statusgeral.nome}
                        />
                      </td>
                      <td>
                        <Flag name={ch.tipo} type={ch.tipo} />
                      </td>
                      <td>
                        <Flag name={ch.setor} type={ch.setor} />
                      </td>
                      <td>
                        <Veiculacao $alterada={ch.veiculacaoalterada}>
                          {ch.datainicio && ch.datafim
                            ? `de ${format(
                              parseISO(ch.datainicio),
                              'dd/MM',
                            )} a ${format(parseISO(ch.datafim), 'dd/MM')} `
                            : '---'}
                          {[
                            Tipo.CHAMADA,
                            Tipo.CAMPANHA,
                            Tipo.APOIO,
                            Tipo.AFINACAO,
                            Tipo.PROMOCIONAL,
                          ].includes(ch.tipo) && (
                              <Button
                                onClick={() => handleEditPeriodoVeiculacao(ch)}
                                fill
                                color="font"
                                round
                                size="xsm"
                                tooltip="Alterar período de veiculação"
                              >
                                <FiEdit2 />
                              </Button>
                            )}
                        </Veiculacao>
                      </td>
                      <td>
                        <Avatar
                          size="40"
                          name={ch.midia?.avaliador?.nome}
                          src={ch.midia?.avaliador?.avatar_url}
                          round
                        />
                      </td>
                      <td>
                        {ch.observacaoexibicao && (
                          <>
                            <Button
                              id={`id_obs_${ch.id}`}
                              fill
                              color="font"
                              round
                              size="xsm"
                              disabled={!ch.observacaoexibicao}
                              onClick={() =>
                                handleObservacaoExibicao(ch.observacaoexibicao)
                              }
                            >
                              <FiMessageCircle />
                            </Button>
                            <Tooltip target={`id_obs_${ch.id}`}>
                              OBS. EXIBIÇÃO
                            </Tooltip>
                          </>
                        )}
                      </td>
                      <td>
                        {ch.midia?.obseng && (
                          <>
                            <Button
                              id={`id_ressalva_eng_${ch.id}`}
                              fill
                              color="font"
                              round
                              size="xsm"
                              disabled={!ch.midia?.obseng}
                              onClick={() => handleRessalva(ch.midia.obseng)}
                            >
                              <FiAlertTriangle />
                            </Button>
                            <Tooltip target={`id_ressalva_eng_${ch.id}`}>
                              RESSALVA ENG.
                            </Tooltip>
                          </>
                        )}
                      </td>
                      <td>
                        {ch.midia?.preview_url && (
                          <>
                            <Button
                              id={`id_assistir_${ch.id}`}
                              fill
                              color="font"
                              round
                              size="xsm"
                              disabled={!ch.midia?.preview_url}
                              onClick={() => handlePlayer(ch.midia)}
                            >
                              <FiPlay />
                            </Button>
                            <Tooltip target={`id_assistir_${ch.id}`}>
                              ASSISTIR
                            </Tooltip>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Scroll>
        ) : (
          <NotFound img={NoSearch2} title="Chamadas">
            Nenhuma CHAMADA encontrada
          </NotFound>
        )}
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
